import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  Sparkles, ArrowRight, ShieldCheck, Zap, HeartHandshake, Star,
  BookOpen, MessageCircle,
} from "lucide-react";
import SEO, { orgSchema, websiteSchema, faqSchema } from "../components/SEO";
import { Reveal, MaskLine, ease } from "../components/Reveal";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { TOOLS } from "../lib/tools";

const FAQS = [
  { question: "What is numerology?", answer: "Numerology is the ancient study of the mystical relationship between numbers and life. At Numoraa, we use your name and birth date to reveal patterns for self-reflection and guidance." },
  { question: "Are Numoraa's tools free?", answer: "Yes — our Numerology Calculator, Lo Shu Grid, Love Calculator and Palm Reading are free to use. Live AI Pandit consultations are affordably priced per minute." },
  { question: "Is my data private and secure?", answer: "Your name and date of birth are used to generate your reading and are not saved by our free tools. Uploaded palm photos and AI Pandit chat messages are stored on our servers. We also use analytics to understand how the site is used. We never sell your personal information." },
  { question: "Do the AI Pandits give guaranteed predictions?", answer: "No. Our AI Pandits offer warm, encouraging numerology guidance for reflection. We never make guaranteed claims or create fear." },
  { question: "Can I use Numoraa on my phone?", answer: "Yes, Numoraa is fully mobile-first and works beautifully on any device." },
];

const BENEFITS = [
  { icon: Sparkles, title: "Self-Awareness", text: "Understand your core nature, strengths and blind spots through your driver and destiny numbers." },
  { icon: HeartHandshake, title: "Better Relationships", text: "See how your numbers interact with loved ones and communicate with more empathy." },
  { icon: Zap, title: "Clarity & Timing", text: "Discover lucky days, colours and directions to bring a little more flow to your decisions." },
  { icon: Star, title: "Purpose & Direction", text: "Reconnect with your life path and the meaningful work you're here to do." },
];

const WHY = [
  { icon: Zap, title: "AI-Powered Depth", text: "Gemini-powered analysis blends ancient numerology with modern intelligence for richer readings." },
  { icon: ShieldCheck, title: "Warm & Ethical", text: "No fear, no guarantees — only kind, honest guidance designed to encourage you." },
  { icon: BookOpen, title: "Learn As You Go", text: "A growing library of clear, beautiful articles helps you understand every number." },
  { icon: MessageCircle, title: "Live AI Pandits", text: "Chat one-on-one with four specialised AI Pandits, any time, at gentle prices." },
];

const STEPS = [
  { n: "01", title: "Share your details", text: "Enter your full name and date of birth — that's all our tools need to begin." },
  { n: "02", title: "AI reads your numbers", text: "We instantly calculate your Mulank, Bhagyank, name number and Lo Shu grid." },
  { n: "03", title: "Receive warm guidance", text: "Get a beautiful, easy-to-understand reading with practical, encouraging insights." },
];

const LATEST = [
  {
    slug: "what-is-mulank-in-numerology-meaning-ruling-planets",
    title: "What Is Mulank in Numerology? Meaning & Ruling Planets",
    text: "Mulank is your birth number, found from the day you were born. Learn its meaning and ruling planets.",
  },
  {
    slug: "mulank-vs-bhagyank-what-is-the-difference",
    title: "Mulank vs Bhagyank: What Is the Difference?",
    text: "Mulank comes from your birth day, Bhagyank from your full date of birth. See how they differ.",
  },
  {
    slug: "the-complete-guide-to-numerology-life-path-numbers-explained",
    title: "The Complete Guide to Numerology: Life Path Numbers Explained",
    text: "How to calculate your Life Path Number and what all 9 numbers and master numbers mean.",
  },
];

function ToolCard({ tool, className = "", featured }) {
  const Icon = tool.icon;
  return (
    <Link
      to={`/tools/${tool.slug}`}
      data-testid={`home-tool-${tool.slug}`}
      className={`group relative overflow-hidden rounded-3xl border border-border bg-card shadow-soft transition-all duration-500 hover:shadow-lift hover:-translate-y-1 ${className}`}
    >
      <div className={`relative ${featured ? "h-56 md:h-full md:min-h-[320px]" : "h-40"} overflow-hidden`}>
        <img src={tool.image} alt={tool.name} loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/70 via-primary/10 to-transparent" />
        <span className="absolute top-4 left-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/90 backdrop-blur">
          <Icon className="h-5 w-5 text-primary" strokeWidth={1.6} />
        </span>
      </div>
      <div className="p-6">
        <p className="text-xs uppercase tracking-[0.2em] text-gold">{tool.tagline}</p>
        <h3 className="mt-2 font-serif text-2xl text-foreground">{tool.name}</h3>
        <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-2">{tool.description}</p>
        <span className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary">
          Open tool <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
}

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scaleBg = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacityBg = useTransform(scrollYProgress, [0, 0.8], [0.5, 0]);

  return (
    <>
      <SEO
        title="India's AI-Powered Numerology Platform"
        description="Numoraa reveals your life numbers through AI-powered numerology tools, palm reading, Lo Shu grid, personalized reports and live AI Pandit consultations."
        path="/"
        schema={[orgSchema, websiteSchema, faqSchema(FAQS)]}
      />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] flex items-center overflow-hidden pt-24 pb-16">
        <motion.div style={{ y: yBg, scale: scaleBg, opacity: opacityBg }} className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1758964297394-1eab6102f9d3?crop=entropy&cs=srgb&fm=webp&q=60&w=900"
            alt="" aria-hidden="true"
            fetchPriority="high"
            decoding="async"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-background/60" />
        </motion.div>
        <motion.div
          aria-hidden
          className="absolute right-[-120px] top-[15%] z-0 h-[420px] w-[420px] rounded-full border border-gold/30 animate-spin-slow"
          style={{ background: "radial-gradient(circle at 30% 30%, rgba(212,175,55,0.10), transparent 60%)" }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 w-full">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-white/70 backdrop-blur px-4 py-1.5 text-xs">
            <Sparkles className="h-3.5 w-3.5 text-gold" />
            <span className="text-muted-foreground">AI-Powered Numerology · Made in India</span>
          </motion.div>

          <h1 className="mt-7 font-serif font-light tracking-tighter leading-[0.95] text-foreground text-5xl sm:text-6xl md:text-7xl lg:text-8xl">
            <MaskLine delay={0.1}>Read the story</MaskLine>
            <MaskLine delay={0.24} className="italic text-primary">written in your</MaskLine>
            <MaskLine delay={0.38}>
              numbers<span className="text-gold">.</span>
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease }}
            className="mt-8 max-w-xl text-lg md:text-xl font-light text-muted-foreground leading-relaxed"
          >
            Warm, intelligent numerology guidance drawn from your name and birth date — decoded by AI, delivered with heart.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link to="/tools/numerology-calculator" data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-primary-foreground transition-all hover:-translate-y-0.5 hover:shadow-gold">
              Get your free reading
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/chat" data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-white/60 backdrop-blur px-7 py-3.5 text-foreground transition-all hover:border-primary">
              Chat with an AI Pandit
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 1 }}
            className="mt-14 flex flex-wrap gap-x-10 gap-y-4 text-sm text-muted-foreground"
          >
            {["500+ readings generated", "5 free tools", "4 AI Pandits", "No sign-up needed"].map((s) => (
              <span key={s} className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-gold" />{s}</span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-border bg-secondary/40 py-6">
        <Marquee speed={30} gradient={false} autoFill>
          {["Numerology", "AI Palmistry", "Lo Shu Grid", "Life Path", "Compatibility", "Lucky Numbers", "Remedies", "Destiny"].map((w) => (
            <span key={w} className="mx-12 font-serif text-3xl md:text-4xl font-light text-muted-foreground/70">
              {w} <span className="text-gold">✦</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* INTRODUCTION */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-32">
        <div className="grid gap-12 md:grid-cols-12 md:gap-16">
          <div className="md:col-span-5">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.2em] text-gold">Introduction</p>
              <h2 className="mt-4 font-serif text-4xl sm:text-5xl font-normal tracking-tight text-foreground leading-tight">
                Ancient wisdom, reimagined for the modern seeker.
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <Reveal delay={0.15}>
              <p className="text-lg font-light text-muted-foreground leading-relaxed">
                For thousands of years, numbers have been used to understand the patterns of a life. Numoraa brings that timeless practice into today — pairing traditional numerology with thoughtful AI to give you readings that are personal, beautiful and genuinely warm.
              </p>
              <p className="mt-6 text-lg font-light text-muted-foreground leading-relaxed">
                No fear. No pressure. Just gentle, honest guidance to help you know yourself a little better.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* POPULAR TOOLS — BENTO */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-8">
        <Reveal>
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-gold">Popular Tools</p>
              <h2 className="mt-3 font-serif text-4xl sm:text-5xl font-normal tracking-tight">Begin your journey</h2>
            </div>
            <Link to="/tools" className="hidden md:inline-flex items-center gap-1.5 text-sm text-primary hover:gap-2.5 transition-all">
              All tools <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
        <div className="grid gap-6 md:grid-cols-12">
          <Reveal className="md:col-span-7" delay={0.05}>
            <ToolCard tool={TOOLS[0]} featured className="h-full" />
          </Reveal>
          <div className="md:col-span-5 grid gap-6">
            <Reveal delay={0.1}><ToolCard tool={TOOLS[1]} /></Reveal>
            <Reveal delay={0.15}><ToolCard tool={TOOLS[2]} /></Reveal>
          </div>
          <Reveal className="md:col-span-6" delay={0.1}><ToolCard tool={TOOLS[3]} /></Reveal>
          <Reveal className="md:col-span-6" delay={0.15}><ToolCard tool={TOOLS[4]} /></Reveal>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-32">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.2em] text-gold">Why Numoraa</p>
          <h2 className="mt-3 font-serif text-4xl sm:text-5xl font-normal tracking-tight max-w-2xl">
            Guidance you can actually trust.
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {WHY.map((w, i) => (
            <Reveal key={w.title} delay={i * 0.08}>
              <div className="h-full rounded-3xl border border-border bg-card p-7 shadow-soft transition-all hover:shadow-lift hover:-translate-y-1">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-secondary">
                  <w.icon className="h-5 w-5 text-primary" strokeWidth={1.6} />
                </span>
                <h3 className="mt-5 font-serif text-2xl">{w.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{w.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* BENEFITS */}
      <section className="bg-primary text-primary-foreground py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="grid gap-12 md:grid-cols-12 md:gap-16 items-start">
            <div className="md:col-span-4">
              <Reveal>
                <p className="text-xs uppercase tracking-[0.2em] text-gold">Benefits of Numerology</p>
                <h2 className="mt-4 font-serif text-4xl sm:text-5xl font-normal tracking-tight leading-tight">
                  Small numbers, meaningful shifts.
                </h2>
              </Reveal>
            </div>
            <div className="md:col-span-7 md:col-start-6 grid gap-8 sm:grid-cols-2">
              {BENEFITS.map((b, i) => (
                <Reveal key={b.title} delay={i * 0.08}>
                  <div className="flex gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/10">
                      <b.icon className="h-5 w-5 text-gold" strokeWidth={1.6} />
                    </span>
                    <div>
                      <h3 className="font-serif text-2xl">{b.title}</h3>
                      <p className="mt-1.5 text-sm text-primary-foreground/70 leading-relaxed">{b.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — manifesto chapters */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-32">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.2em] text-gold">How It Works</p>
          <h2 className="mt-3 font-serif text-4xl sm:text-5xl font-normal tracking-tight">Three gentle steps</h2>
        </Reveal>
        <div className="mt-16 space-y-16 md:space-y-24">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.05}>
              <div className="relative grid md:grid-cols-12 items-center gap-6">
                <span className="pointer-events-none select-none font-serif text-[7rem] md:text-[11rem] leading-none text-gold/15 md:col-span-4">
                  {s.n}
                </span>
                <div className="md:col-span-7 md:col-start-6">
                  <h3 className="font-serif text-3xl md:text-4xl text-foreground">{s.title}</h3>
                  <p className="mt-3 max-w-md text-lg font-light text-muted-foreground leading-relaxed">{s.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FROM THE JOURNAL: CTA band + latest articles */}
      <section className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2rem] border border-border bg-secondary/50 p-10 md:p-16">
            <div className="relative z-10 max-w-xl">
              <p className="text-xs uppercase tracking-[0.2em] text-gold">From the journal</p>
              <h2 className="mt-3 font-serif text-4xl sm:text-5xl font-normal tracking-tight">Learn the language of numbers</h2>
              <p className="mt-4 text-lg font-light text-muted-foreground">Clear, beautifully written guides to help you understand every number, grid and line.</p>
              <Link to="/blog" data-testid="home-blog-cta"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-primary-foreground transition-all hover:-translate-y-0.5 hover:shadow-gold">
                Read the blog <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="pointer-events-none absolute -right-16 -bottom-16 h-72 w-72 rounded-full bg-gold/10 blur-2xl" />
          </div>
        </Reveal>

        <div className="mt-8 grid gap-6 md:grid-cols-3" data-testid="home-latest-posts">
          {LATEST.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.08}>
              <Link
                to={`/blog/${p.slug}`}
                className="group block h-full rounded-3xl border border-border bg-card p-7 shadow-soft transition-all hover:shadow-lift hover:-translate-y-1"
              >
                <p className="text-xs uppercase tracking-[0.2em] text-gold">Numerology</p>
                <h3 className="mt-3 font-serif text-2xl text-foreground">{p.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.text}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary">
                  Read article <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-5 md:px-8 pt-24 md:pt-32 pb-24 md:pb-32">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.2em] text-gold text-center">FAQ</p>
          <h2 className="mt-3 font-serif text-4xl sm:text-5xl font-normal tracking-tight text-center">Questions, answered</h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Accordion type="single" collapsible className="mt-10" data-testid="home-faq">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-border">
                <AccordionTrigger className="text-left font-serif text-xl hover:no-underline py-6">{f.question}</AccordionTrigger>
                <AccordionContent className="text-base text-muted-foreground leading-relaxed pb-6">{f.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </section>
    </>
  );
}